import { useQueryClient } from '@tanstack/vue-query'
import { MaybeRef } from 'vue'
import { useNotification } from './useNotification'
import { DataWithPagination } from '../types'

export type TInfiniteQueryData<T> = {
  pages: DataWithPagination<T>[]
}

export const useServiceInfiniteQueryHandlers = <
  T extends { id?: number },
>(settings: {
  model: string
  filterParams: MaybeRef<Record<string, string>> | MaybeRef<string>
}) => {
  const { model, filterParams } = settings
  const queryClient = useQueryClient()

  const { successWithActionNotification } = useNotification()

  const addRecord = (payload: T) => {
    const pages = queryClient.getQueryData<TInfiniteQueryData<T>>([
      model,
      filterParams,
    ])

    if (pages) {
      const firstPageData = pages.pages[0].data
      const updatedFirstPageData = [payload, ...firstPageData]
      const updatedPages = [
        { ...pages.pages[0], data: updatedFirstPageData },
        ...pages.pages.slice(1),
      ]

      queryClient.setQueryData<TInfiniteQueryData<T>>([model, filterParams], {
        ...pages,
        pages: updatedPages,
      })
    }

    successWithActionNotification('ADDED', model)
  }

  const deleteRecord = (payload: T) => {
    const pages = queryClient.getQueryData<TInfiniteQueryData<T>>([
      model,
      filterParams,
    ])

    if (pages) {
      const updatedPages = pages.pages.map((page) => ({
        ...page,
        data: page.data.filter((record) => record.id !== payload.id),
      }))

      queryClient.setQueryData<TInfiniteQueryData<T>>([model, filterParams], {
        ...pages,
        pages: updatedPages,
      })
    }

    successWithActionNotification('DELETED', model)
  }

  const deleteRecords = (payload: Array<number>) => {
    const pages = queryClient.getQueryData<TInfiniteQueryData<T>>([
      model,
      filterParams,
    ])

    if (pages) {
      const updatedPages = pages.pages.map((page) => ({
        ...page,
        data: page.data.filter((record) => !payload.includes(record?.id ?? 0)),
      }))

      queryClient.setQueryData<TInfiniteQueryData<T>>([model, filterParams], {
        ...pages,
        pages: updatedPages,
      })
    }

    successWithActionNotification('DELETED', model)
  }

  const updateRecord = (payload: T) => {
    const pages = queryClient.getQueryData<TInfiniteQueryData<T>>([
      model,
      filterParams,
    ])

    if (pages) {
      const updatedPages = pages.pages.map((page) => {
        const dataIndex = page.data.findIndex(
          (record) => record.id === payload.id,
        )
        if (dataIndex !== -1) {
          const updatedData = [
            ...page.data.slice(0, dataIndex),
            { ...page.data[dataIndex], ...payload },
            ...page.data.slice(dataIndex + 1),
          ]

          return { ...page, data: updatedData }
        }

        return page
      })

      queryClient.setQueryData<TInfiniteQueryData<T>>([model, filterParams], {
        ...pages,
        pages: updatedPages,
      })
    }

    successWithActionNotification('UPDATED', model)
  }

  const invalidateQueries = async (key?: any) => {
    await queryClient.invalidateQueries({ queryKey: [key ?? model] })
  }

  return {
    addRecord,
    deleteRecord,
    deleteRecords,
    updateRecord,
    invalidateQueries,
  }
}
